import React, { useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import { useRealtime } from "@/lib/realtime";
import Layout, { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Radio, Users, Siren, ShieldAlert, Building2, Archive, Send, MapPin, CheckCheck, AlertTriangle, Lock, ChevronDown } from "lucide-react";
import { messageBeeps, hazardTone } from "@/lib/audio";

const QUICK = ["10-4", "En Route", "On Scene", "Request Backup"];
const CMD_ROLES = ["supervisor", "watch_commander", "records_supervisor", "admin"];

const CHANNELS = [
  { key: "dispatch", label: "Dispatch Chat", icon: Radio, color: "#3B82F6" },
  { key: "unit", label: "Unit Messaging", icon: Users, color: "#10B981" },
  { key: "incident", label: "Incident Chat", icon: Siren, color: "#EF4444" },
  { key: "command", label: "Command Channel", icon: ShieldAlert, color: "#8B5CF6", roles: CMD_ROLES },
  { key: "agency", label: "Agency / Multiagency", icon: Building2, color: "#F59E0B" },
  { key: "archived", label: "Archived Communications", icon: Archive, color: "#6B7280" },
];

export default function Comms() {
  const { user } = useAuth();
  const myUnit = user?.badge || "";
  const [channel, setChannel] = useState("dispatch");
  const [rooms, setRooms] = useState([]);
  const [room, setRoom] = useState(null);
  const [dmUnit, setDmUnit] = useState(myUnit);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [priority, setPriority] = useState(false);
  const [units, setUnits] = useState([]);
  const endRef = useRef(null);
  // Incoming-message tone: prime the seen-set on each thread switch (no tone),
  // then beep on every NEW message from someone else while the page is open.
  const seenIds = useRef(new Set());
  const primeRef = useRef(true);

  const channels = CHANNELS.filter((c) => !c.roles || c.roles.includes(user?.role));

  const load = async () => {
    try {
      const params = { limit: 120 };
      if (channel === "archived") params.everything = true;
      else params.channel = channel;
      if (channel === "unit") params.unit = dmUnit || myUnit;
      if (channel === "incident") { if (!room) { setMsgs([]); return; } params.incident_cc = room; }
      const { data } = await client.get("/messages", { params });
      if (primeRef.current) {
        primeRef.current = false;
      } else {
        const fresh = data.filter((m) => !seenIds.current.has(m.id) && m.from_name !== user?.name);
        if (fresh.length) {
          try { fresh.some((m) => m.priority) ? hazardTone() : messageBeeps(); } catch { /* audio blocked pre-interaction */ }
        }
      }
      data.forEach((m) => seenIds.current.add(m.id));
      setMsgs(data);
    } catch (e) { if (e.response?.status !== 403) setMsgs([]); }
  };

  useEffect(() => { client.get("/comms/rooms").then((r) => setRooms(r.data)).catch(() => {}); }, []);
  useEffect(() => { client.get("/units").then((r) => setUnits(r.data)).catch(() => {}); }, []);
  useEffect(() => { primeRef.current = true; }, [channel, room, dmUnit]);
  useEffect(() => { load(); const iv = setInterval(load, 2000); return () => clearInterval(iv); }, [channel, room, dmUnit]); // eslint-disable-line
  useRealtime(["messages", "incidents"], () => load());
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (extra = {}) => {
    const payload = {
      channel, text: extra.text ?? text, priority,
      from_unit: myUnit || null,
      to_unit: channel === "unit" ? (dmUnit || null) : null,
      incident_cc: channel === "incident" ? room : null,
      quick_action: extra.quick_action || null,
      msg_type: extra.msg_type || "text",
      gps: extra.gps || null,
      scope: channel === "incident" ? "incident" : channel === "unit" ? "unit" : "broadcast",
    };
    if (!payload.text && !payload.quick_action && !payload.gps) { toast.error("Type a message"); return; }
    try {
      await client.post("/messages", payload);
      setText(""); setPriority(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
  };

  const shareGps = () => {
    if (!navigator.geolocation) { toast.error("Geolocation unavailable"); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => send({ gps: { lat: p.coords.latitude, lng: p.coords.longitude }, msg_type: "location", text: "📍 Shared my location" }),
      () => toast.error("Location permission denied"));
  };

  const ack = async (m) => { try { await client.post(`/messages/${m.id}/ack`); load(); } catch { /* noop */ } };

  const activeChan = channels.find((c) => c.key === channel);

  return (
    <Layout>
      <PageHeader title="Secure CAD/MDT Communications"
        sub="CJIS-controlled · env-isolated · audited · sender/unit identified · retention per agency policy"
        right={<span className="font-mono text-[10px] text-[#F59E0B] flex items-center gap-1.5"><Lock size={12} /> ENCRYPTED CHANNEL</span>} />
      <div className="flex-1 flex overflow-hidden">
        {/* channel rail */}
        <div className="w-56 shrink-0 border-r border-[#2C2D35] bg-[#0d0e11] overflow-y-auto">
          {channels.map((c) => (
            <button key={c.key} data-testid={`comms-channel-${c.key}`} onClick={() => { setChannel(c.key); setRoom(null); }}
              className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm font-head border-l-2 transition-colors ${channel === c.key ? "bg-[#141518]" : "border-transparent text-[#9CA3AF] hover:bg-[#141518]"}`}
              style={channel === c.key ? { borderColor: c.color, color: "#fff" } : {}}>
              <c.icon size={15} style={{ color: c.color }} /> {c.label}
            </button>
          ))}
          {channel === "incident" && (
            <div className="border-t border-[#2C2D35] mt-1 pt-1">
              <div className="px-3 py-1.5 font-mono text-[9px] text-[#6B7280] uppercase">Call Rooms ({rooms.length})</div>
              {rooms.map((r) => (
                <button key={r.cc} data-testid={`comms-room-${r.cc}`} onClick={() => setRoom(r.cc)}
                  className={`w-full text-left px-3 py-2 text-xs border-l-2 ${room === r.cc ? "border-[#EF4444] bg-[#141518] text-white" : "border-transparent text-[#9CA3AF] hover:bg-[#141518]"}`}>
                  <div className="font-mono text-[10px] text-[#EF4444]">{r.cc}</div>
                  <div className="truncate">{r.label}</div>
                  <div className="font-mono text-[9px] text-[#6B7280]">{r.units.join(", ") || "unassigned"}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* thread */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="px-4 py-2 border-b border-[#2C2D35] bg-[#0d0e11] flex items-center gap-2">
            {activeChan && <activeChan.icon size={15} style={{ color: activeChan.color }} />}
            <span className="font-head text-sm text-white">{activeChan?.label}{channel === "incident" && room ? ` · ${room}` : ""}</span>
            {channel === "unit" && (
              <UnitPicker units={units} value={dmUnit} onPick={setDmUnit} />
            )}
          </div>

          <div data-testid="comms-thread" className="flex-1 overflow-y-auto p-4 space-y-2 grid-lines">
            {msgs.length === 0 && <div className="font-mono text-xs text-[#6B7280] text-center mt-8">
              {channel === "incident" && !room ? "Select a call room to view its secure chat." : "No messages yet."}</div>}
            {msgs.map((m) => {
              const mine = m.from_name === user?.name;
              return (
                <div key={m.id} data-testid={`comms-msg-${m.id}`} className={`max-w-[75%] ${mine ? "ml-auto" : ""}`}>
                  <div className={`border px-3 py-2 ${m.priority ? "border-[#DC2626] bg-[#1a0b0c]" : "border-[#2C2D35] bg-[#141518]"}`}>
                    <div className="flex items-center gap-2 mb-0.5">
                      {m.priority && <AlertTriangle size={11} className="text-[#DC2626]" />}
                      <span className="font-mono text-[10px] text-[#9CA3AF]">{m.from_unit || m.from_name} · {m.from_role?.replace(/_/g, " ")}</span>
                      <span className="ml-auto font-mono text-[9px] text-[#6B7280]">{new Date(m.ts).toLocaleTimeString()}</span>
                    </div>
                    {m.quick_action && <span className="inline-block font-mono text-[10px] font-bold text-[#10B981] border border-[#10B981]/50 px-1.5 py-0.5 mb-1">{m.quick_action}</span>}
                    {m.text && <div className="text-sm text-[#F3F4F6]">{m.text}</div>}
                    {m.gps && <a href={`https://maps.google.com/?q=${m.gps.lat},${m.gps.lng}`} target="_blank" rel="noreferrer" className="text-xs text-[#3B82F6] flex items-center gap-1 mt-1"><MapPin size={11} /> {m.gps.lat.toFixed(4)}, {m.gps.lng.toFixed(4)}</a>}
                    <div className="flex items-center gap-2 mt-1">
                      <span className="font-mono text-[9px] text-[#6B7280]">✓ read {(m.reads || []).length} · ack {(m.acks || []).length}</span>
                      {!mine && !(m.acks || []).includes(user?.name) && (
                        <button data-testid={`comms-ack-${m.id}`} onClick={() => ack(m)} className="font-mono text-[9px] text-[#10B981] flex items-center gap-0.5"><CheckCheck size={11} /> ACK</button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>

          {channel !== "archived" && !(channel === "incident" && !room) && (
            <div className="border-t border-[#2C2D35] bg-[#0d0e11] p-3 relative z-[10000]">
              <div className="flex flex-wrap gap-1.5 mb-2">
                {QUICK.map((q) => (
                  <button key={q} data-testid={`comms-quick-${q.replace(/[^a-z0-9]/gi, "").toLowerCase()}`} onClick={() => send({ quick_action: q, text: "" })}
                    className="px-2.5 py-1 text-xs font-head bg-[#141518] border border-[#2C2D35] hover:border-[#10B981] text-[#9CA3AF] hover:text-white">{q}</button>
                ))}
                <button data-testid="comms-gps" onClick={shareGps} className="px-2.5 py-1 text-xs font-head bg-[#141518] border border-[#2C2D35] hover:border-[#3B82F6] text-[#9CA3AF] flex items-center gap-1"><MapPin size={12} /> Share GPS</button>
                <button data-testid="comms-priority" onClick={() => setPriority(!priority)}
                  className={`px-2.5 py-1 text-xs font-head border flex items-center gap-1 ${priority ? "bg-[#DC2626] text-white border-[#DC2626]" : "bg-[#141518] border-[#2C2D35] text-[#9CA3AF]"}`}><AlertTriangle size={12} /> Priority</button>
              </div>
              <div className="flex gap-2">
                <input data-testid="comms-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Type a secure message…" className="flex-1 bg-[#141518] border border-[#2C2D35] px-3 py-2 text-sm outline-none focus:border-[#3B82F6] font-mono text-white" />
                <button data-testid="comms-send" onClick={() => send()} className="px-4 bg-[#3B82F6] text-white font-head text-sm flex items-center gap-1.5"><Send size={14} /> Send</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

// Searchable recipient picker: dispatchers select a unit number / officer name
// from a dropdown instead of typing blindly.
function UnitPicker({ units, value, onPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ql = q.trim().toLowerCase();
  const shown = (ql ? units.filter((u) => `${u.unit_id} ${u.personnel || ""}`.toLowerCase().includes(ql)) : units).slice(0, 60);
  const STATUS_DOT = { available: "#10B981", dispatched: "#F59E0B", enroute: "#F59E0B", onscene: "#EF4444", transporting: "#8B5CF6", out: "#6B7280" };
  const sel = units.find((u) => u.unit_id === value);
  return (
    <div className="ml-auto relative">
      <button data-testid="comms-dm-unit" type="button" onClick={() => { setOpen(!open); setQ(""); }}
        className="flex items-center gap-1.5 w-52 bg-[#141518] border border-[#2C2D35] px-2 py-1 text-xs font-mono text-left hover:border-[#10B981]">
        <Users size={12} className="text-[#10B981] shrink-0" />
        <span className="flex-1 truncate text-white">{value ? `${value}${sel?.personnel ? " — " + sel.personnel : ""}` : "Select unit / officer…"}</span>
        <ChevronDown size={12} className="text-[#6B7280] shrink-0" />
      </button>
      {open && (
        <div data-testid="comms-unit-dropdown" className="absolute right-0 top-full mt-1 w-72 max-h-72 overflow-y-auto border border-[#2C2D35] bg-[#0d0e11] z-30 shadow-xl">
          <div className="sticky top-0 bg-[#0d0e11] p-1.5 border-b border-[#2C2D35]">
            <input autoFocus data-testid="comms-unit-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Filter by unit # or name…"
              className="w-full bg-[#141518] border border-[#2C2D35] px-2 py-1 text-xs font-mono outline-none focus:border-[#10B981] text-white" />
          </div>
          {shown.map((u) => (
            <button key={u.unit_id} data-testid={`comms-unit-pick-${u.unit_id}`} type="button"
              onClick={() => { onPick(u.unit_id); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-xs font-mono text-left hover:bg-[#141518] border-b border-[#1c1d22] ${value === u.unit_id ? "bg-[#10B981]/10 text-white" : "text-[#C9CDD4]"}`}>
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: STATUS_DOT[u.status] || "#6B7280" }} />
              <b className="text-white">{u.unit_id}</b>
              <span className="truncate">{u.personnel || "—"}</span>
              <span className="ml-auto text-[9px] uppercase text-[#6B7280]">{u.type} · {u.status}</span>
            </button>
          ))}
          {shown.length === 0 && <div className="px-3 py-2 text-[10px] font-mono text-[#6B7280]">No unit matches “{q}”.</div>}
        </div>
      )}
    </div>
  );
}
